import { useNavigate, useParams } from "react-router-dom"
import { useCallback, useContext, useEffect, useState } from "react"
import { Button, Col, DatePicker, Divider, Flex, Input, InputNumber, List, Modal, Row, Spin } from "antd"
import { PlusCircleOutlined, ProfileOutlined } from "@ant-design/icons"
import { CompanyContext, CompanyRequestType, CompanyResponseType, UpdateCompanyContext } from "../../../context/companyContext"
import dayjs from 'dayjs'
import { DepartmentRequestType } from "../../../context/departmentContext"
import TextArea from "antd/es/input/TextArea"
import { PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../util/helper"
import { isValidEmail } from "../../../utils/validation"
import { useTranslation } from "react-i18next"

const DEFAULT_DEPARTMENT: DepartmentRequestType = { id: null, name: '', brand_name: '', memo: '' }
const DUPLICATE_DEPARTMENT_ERROR_CODE = "DEPARTMENT_NAME_DUPLICATED"

type CompanySaveError = Error & {
  status?: number
  responseMessage?: string | null
  errorCode?: string | null
}

const normalizeDepartments = (departments: unknown): DepartmentRequestType[] => {
  const rawList = (() => {
    if (Array.isArray(departments)) {
      return departments
    }
    if (departments && typeof departments === 'object' && 'data' in departments) {
      const data = (departments as { data?: unknown }).data
      if (Array.isArray(data)) {
        return data
      }
    }
    return []
  })()

  if (rawList.length === 0) {
    return [{ ...DEFAULT_DEPARTMENT }]
  }

  return rawList.map((dept: any) => ({
    id: dept?.id ?? null,
    name: dept?.name ?? '',
    brand_name: dept?.brand_name ?? '',
    memo: typeof dept?.memo === 'string' ? dept.memo : (dept?.memo ?? ''),
  }))
}

const cleanCompanyInfo = (data: CompanyRequestType): CompanyRequestType => {
  const cleanedData: CompanyRequestType = { ...data }

  // 1. 日付のフォーマットとnull変換
  cleanedData.contract_start_date = data.contract_start_date
    ? dayjs(data.contract_start_date).format('YYYY-MM-DD')
    : null
  cleanedData.contract_end_date = data.contract_end_date
    ? dayjs(data.contract_end_date).format('YYYY-MM-DD')
    : null

  // 2. 契約金額のクリーニング
  cleanedData.contract_amount =
    data.contract_amount !== null && data.contract_amount !== undefined
      ? parseFloat(data.contract_amount.toString().replace(/,/g, ''))
      : null

  // 3. 部署情報のクリーニング
  if (Array.isArray(data.departments)) {
    cleanedData.departments = data.departments
      .filter(department => department.brand_name !== '' || department.name !== '')
      .map(department => ({
        ...(department.id !== undefined && department.id !== null && { id: department.id }),
        name: department.name,
        brand_name: department.brand_name,
        memo: (department.memo === '' || department.memo === undefined) ? null : department.memo,
      }))

    if (cleanedData.departments.length === 0) {
      cleanedData.departments = []
    }
  } else {
    cleanedData.departments = []
  }

  // 4. その他のプロパティのクリーニング（空文字列、undefinedをnullに変換）
  for (const key of Object.keys(cleanedData) as (keyof CompanyRequestType)[]) {
    const value = cleanedData[key]

    if (value === '' || value === undefined) {
      if (typeof value !== 'number' || value !== 0) {
        cleanedData[key] = null
      }
    }
  }

  return cleanedData
}

export function CompanyManage() {
  const { registerCompany, updateCompany, refreshCompany } = useContext(UpdateCompanyContext)
  const { company } = useContext(CompanyContext)
  const { companyId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const initializeCompanyInfo = (companyData?: Partial<CompanyRequestType> | CompanyResponseType | null): CompanyRequestType => {
    const data = (companyData ?? {}) as Partial<CompanyRequestType>
    const departmentsSource = (companyData as { departments?: unknown })?.departments

    return {
      company_name: data.company_name || '',
      address: data.address || '',
      phone_number: data.phone_number || '',
      post_code: data.post_code || undefined,
      toyoshima_sales_department: data.toyoshima_sales_department || '',
      toyoshima_sales_rep_name: data.toyoshima_sales_rep_name || '',
      company_memo: data.company_memo || '',
      planned_site_name: data.planned_site_name || '',
      planned_site_address: data.planned_site_address || '',
      planned_site_phone: data.planned_site_phone || undefined,
      planned_site_post_code: data.planned_site_post_code || undefined,
      planned_site_email: data.planned_site_email || '',
      planned_site_contact_name: data.planned_site_contact_name || '',
      planned_site_contact_position: data.planned_site_contact_position || '',
      contact_department: data.contact_department || '',
      contact_name: data.contact_name || '',
      contact_position: data.contact_position || '',
      contact_email: data.contact_email || '',
      contact_phone: data.contact_phone || '',
      billing_department: data.billing_department || '',
      billing_contact: data.billing_contact || '',
      billing_position: data.billing_position || '',
      billing_email: data.billing_email || '',
      billing_phone: data.billing_phone || '',
      contract_start_date: data.contract_start_date || '',
      contract_end_date: data.contract_end_date || '',
      contract_amount: data.contract_amount || 0,
      departments: normalizeDepartments(departmentsSource),
    }
  }

  const [companyInfo, setCompanyInfo] = useState<CompanyRequestType>(initializeCompanyInfo())
  const [errors, setErros] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isPageReady, setIsPageReady] = useState(companyId === 'register')

  const isDuplicateDepartmentError = useCallback((error: unknown) => {
    if (!(error instanceof Error)) {
      return false
    }

    const companySaveError = error as CompanySaveError
    return companySaveError.errorCode === DUPLICATE_DEPARTMENT_ERROR_CODE
  }, [])

  useEffect(() => {
    if (!company) return
    if (companyId === 'register') {
      setCompanyInfo(initializeCompanyInfo({}))
    } else {
      setCompanyInfo(initializeCompanyInfo(company))
    }
  }, [company, companyId])

  useEffect(() => {
    let isCancelled = false

    const validateAndLoadCompanyManage = async () => {
      if (!companyId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
        return
      }

      if (companyId === 'register') {
        setIsPageReady(true)
        return
      }

      setIsPageReady(false)

      try {
        const companyResult = await refreshCompany(companyId)

        if (isCancelled) {
          return
        }

        if (shouldStopForInvalidContextFetchResult(companyResult, navigate)) {
          return
        }

        setIsPageReady(true)
      } catch (error) {
        console.error("Error validating company manage.", error)
        if (isCancelled) {
          return
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
      }
    }

    validateAndLoadCompanyManage()

    return () => {
      isCancelled = true
    }
  }, [companyId, navigate, refreshCompany])

  const addDepartment = useCallback(() => {
    setCompanyInfo((prev) => ({ ...prev, departments: [...(prev.departments || []), { ...DEFAULT_DEPARTMENT }] }))
  }, [])

  const handleInputChange = useCallback((index: number, key: keyof DepartmentRequestType, value: string) => {
    setCompanyInfo((prev) => {
      const newDepartments = [...(prev.departments || [])]
      newDepartments[index] = { ...newDepartments[index], [key]: value }
      return { ...prev, departments: newDepartments }
    })
  }, [])

  const onClickBack = useCallback(() => { navigate(-1) }, [navigate])

  const onClickDelete = useCallback((index: number) => {
    const newDepartments = [...(companyInfo.departments || [])]
    newDepartments.splice(index, 1)
    setCompanyInfo({ ...companyInfo, departments: newDepartments })
  }, [companyInfo])

  const onClickSubmit = useCallback(async () => {
    if (!companyId) return

    const newErrors: Record<string, boolean> = {}
    if (!companyInfo.contact_email) {
      newErrors.contact_email = true
    }

    if (Object.keys(newErrors).length > 0) {
      setErros(newErrors)
      Modal.error({
        content:
          <div className="flex flex-col">
            <span>{t("company.validation.contactEmailRequired")}</span>
          </div>,
        onOk() { },
        centered: true
      })
      return
    }

    let deptInComplete = false
    companyInfo.departments?.forEach((dept, i) => {
      if (!dept.brand_name) {
        newErrors[`department_${i}_brand_name`] = true
        deptInComplete = true
      }
      if (!dept.name) {
        newErrors[`department_${i}_name`] = true
        deptInComplete = true
      }
    })
    if (deptInComplete) {
      setErros(newErrors)
      Modal.error({ content: t("company.validation.departmentRequired"), onOk() { }, centered: true })
      return
    }

    const phoneReg = /^[0-9]{10,11}$/
    const phoneFields: (keyof CompanyRequestType)[] = [
      'phone_number', 'contact_phone', 'billing_phone', 'planned_site_phone'
    ]

    let phoneError = false
    phoneFields.forEach(key => {
      const value = companyInfo[key]
      if (typeof value === 'string' && value && !phoneReg.test(value)) { // 値がある場合のみチェック
        newErrors[key] = true
        phoneError = true
      }
    })

    if (phoneError) {
      setErros(newErrors)
      Modal.error({ content: t("company.validation.phoneNumberDigits"), onOk() { }, centered: true })
      return
    }

    const emailFields: (keyof CompanyRequestType)[] = [
      'contact_email', 'billing_email', 'planned_site_email'
    ]

    let emailError = false
    emailFields.forEach(key => {
      const value = companyInfo[key]
      if (typeof value === 'string' && value && !isValidEmail(value)) { // 値がある場合のみチェック
        newErrors[key] = true
        emailError = true
      }
    })

    if (emailError) {
      setErros(newErrors)
      Modal.error({ content: t("company.validation.emailInvalid"), onOk() { }, centered: true })
      return
    }

    const postCodeReg = /^[0-9]{7}$/
    const postCodeFields: (keyof CompanyRequestType)[] = [
      'post_code', 'planned_site_post_code'
    ]

    let postCodeError = false
    postCodeFields.forEach(key => {
      const value = companyInfo[key]
      const stringValue = typeof value === 'number' ? value.toString() : value
      if (typeof stringValue === 'string' && stringValue && !postCodeReg.test(stringValue)) {
        newErrors[key] = true
        postCodeError = true
      }
    })

    if (postCodeError) {
      setErros(newErrors)
      Modal.error({ content: t("company.validation.postCodeDigits"), onOk() { }, centered: true })
      return
    }

    setErros({})

    setIsLoading(true)

    try {

      const cleaned = cleanCompanyInfo(companyInfo)

      if (companyId === 'register') {
        await registerCompany(cleaned)
        Modal.success({ content: t("company.messages.registerSucceeded"), onOk() { navigate(-1) }, centered: true })
      } else {
        await updateCompany(companyId, cleaned)
        Modal.success({ content: t("company.messages.updateSucceeded"), onOk() { navigate(PATH_INFO.COMPANY_INFO.PATH.replace(':companyId', companyId)) }, centered: true })
      }
    } catch (error) {
      console.error("Operation failed:", error)
      if (isDuplicateDepartmentError(error)) {
        Modal.error({ content: t("company.messages.duplicateDepartment"), centered: true })
        return
      }

      Modal.error({ content: t("messages.processFailedRetry"), centered: true })
    } finally {
      setIsLoading(false)
    }
  }, [companyId, companyInfo, registerCompany, updateCompany, navigate, isDuplicateDepartmentError, t])

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip={t("common.loadingWithDots")} />
      </Flex>
    )
  }

  return (
    <div className="relative">
      {isLoading && (
        <Spin spinning={isLoading} tip={t("common.processing")} size="large" fullscreen />
      )}
      <div>
        <div className="flex w-full space-x-5 items-center">
          <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">
            {companyId === 'register' ? t("company.registerPage") : t("company.editPage")}
          </p>
        </div>
        <Divider />
        <CompanyInput
          companyInfo={companyInfo}
          setCompanyInfo={setCompanyInfo}
          errors={errors}
        />
        <Divider />
        <div className="flex p-2 text-xl mb-3">
          <ProfileOutlined className="text-[#1677ff]" />
          <span className="pl-2">{companyId === 'register' ? t("company.departmentRegister") : t("company.departmentEdit")}</span>
        </div>
        <Row justify={'center'} align={'top'} className="w-full" gutter={[24, 24]}>
          {companyInfo.departments?.map((item, index) => (
            <Col key={index}>
              <List
                bordered
                header={<div className="text-center font-bold">{index + 1}</div>}
                footer={<div className="text-center"><Button variant="outlined" danger onClick={() => onClickDelete(index)}>{t("common.delete")}</Button></div>}
                dataSource={[
                  { key: 'brand_name', label: t("company.brandName"), value: item.brand_name, type: 'text' },
                  { key: 'name', label: t("company.departmentName"), value: item.name, type: 'text' },
                  { key: 'memo', label: t("company.departmentMemo"), value: item.memo, type: 'textarea' },
                ]}
                renderItem={(department) => (
                  <List.Item style={{ background: '#FFFFFF' }}>
                    {department.type === 'text' ?
                      (<Input
                        placeholder={department.label}
                        type={department.type}
                        value={department.value || ''}
                        status={errors[`department_${index}_${department.key}`] ? 'error' : undefined}
                        onChange={(e) => handleInputChange(index, department.key as keyof DepartmentRequestType, e.target.value)}
                      />
                      )
                      :
                      (<TextArea
                        rows={6}
                        placeholder={department.label}
                        value={department.value as string}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                          handleInputChange(index, department.key as keyof DepartmentRequestType, e.target.value)
                        }}
                      />)
                    }
                  </List.Item>
                )}
                className="overflow-hidden bg-[#FAFAFA]"
              />
            </Col>
          ))}
        </Row>
        <Flex vertical align="center" className="h-full mt-5">
          <Button variant="text" color="primary" onClick={addDepartment} icon={<PlusCircleOutlined />}>{t("company.addDepartment")}</Button>
        </Flex>
        <Divider />
        <Flex gap="large" align="center" justify="center">
          <Button variant="outlined" color="primary" size="large" onClick={onClickBack} disabled={isLoading}>{t("common.back")}</Button>
          <Button type="primary" size="large" onClick={onClickSubmit} loading={isLoading}>{companyId === 'register' ? t("common.register") : t("common.update")}</Button>
        </Flex>
      </div>
    </div>
  )
}

function CompanyInput(
  {
    companyInfo,
    setCompanyInfo,
    errors
  }: {
    companyInfo: CompanyRequestType,
    setCompanyInfo: React.Dispatch<React.SetStateAction<CompanyRequestType>>
    errors: Record<string, boolean>
  }) {
  const { t } = useTranslation()

  const COMPANY_DATA_PLACEHOLDER = [
    {
      title: t("company.companyInfo"),
      column: [
        { label: t("company.companyName"), type: 'text', value: companyInfo.company_name, name: 'company_name' },
        { label: t("company.address"), type: 'text', value: companyInfo.address, name: 'address' },
        { label: t("company.phoneNumberNoHyphen"), type: 'text', value: companyInfo.phone_number, name: 'phone_number' },
        { label: t("company.postCodeNoHyphen"), type: 'text', value: companyInfo.post_code, name: 'post_code' },
        { label: t("company.toyoshimaSalesDepartment"), type: 'text', value: companyInfo.toyoshima_sales_department, name: 'toyoshima_sales_department' },
        { label: t("company.toyoshimaSalesRepName"), type: 'text', value: companyInfo.toyoshima_sales_rep_name, name: 'toyoshima_sales_rep_name' },
      ]
    },
    {
      title: t("company.plannedSite"),
      column: [
        { label: t("company.siteName"), type: 'text', value: companyInfo.planned_site_name, name: 'planned_site_name' },
        { label: t("company.address"), type: 'text', value: companyInfo.planned_site_address, name: 'planned_site_address' },
        { label: t("company.phoneNumberNoHyphen"), type: 'text', value: companyInfo.planned_site_phone, name: 'planned_site_phone' },
        { label: t("company.postCodeNoHyphen"), type: 'text', value: companyInfo.planned_site_post_code, name: 'planned_site_post_code' },
        { label: t("company.email"), type: 'email', value: companyInfo.planned_site_email, name: 'planned_site_email' },
        { label: t("company.contactName"), type: 'text', value: companyInfo.planned_site_contact_name, name: 'planned_site_contact_name' },
        { label: t("company.contactPosition"), type: 'text', value: companyInfo.planned_site_contact_position, name: 'planned_site_contact_position' },
      ]
    },
    {
      title: t("company.contact"),
      column: [
        { label: t("company.departmentName"), type: 'text', value: companyInfo.contact_department, name: 'contact_department' },
        { label: t("company.name"), type: 'text', value: companyInfo.contact_name, name: 'contact_name' },
        { label: t("company.position"), type: 'text', value: companyInfo.contact_position, name: 'contact_position' },
        { label: t("company.email"), type: 'email', value: companyInfo.contact_email, name: 'contact_email' },
        { label: t("company.phoneNumberNoHyphen"), type: 'text', value: companyInfo.contact_phone, name: 'contact_phone' }
      ]
    },
    {
      title: t("company.billingInfo"),
      column: [
        { label: t("company.billingDepartment"), type: 'text', value: companyInfo.billing_department, name: 'billing_department' },
        { label: t("company.contactName"), type: 'text', value: companyInfo.billing_contact, name: 'billing_contact' },
        { label: t("company.contactPosition"), type: 'text', value: companyInfo.billing_position, name: 'billing_position' },
        { label: t("company.email"), type: 'email', value: companyInfo.billing_email, name: 'billing_email' },
        { label: t("company.phoneNumberNoHyphen"), type: 'text', value: companyInfo.billing_phone, name: 'billing_phone' }
      ]
    },
    {
      title: t("company.contractPeriod"),
      column: [
        { label: t("company.startDate"), type: 'date', value: companyInfo.contract_start_date, name: 'contract_start_date' },
        { label: t("company.endDate"), type: 'date', value: companyInfo.contract_end_date, name: 'contract_end_date' },
        { label: t("company.amount"), type: 'money', prefix: '￥', suffix: 'JPY', value: companyInfo.contract_amount, name: 'contract_amount' }
      ]
    },
    {
      title: t("company.memo"),
      column: [
        { label: t("company.memo"), type: 'textarea', value: companyInfo.company_memo, name: 'company_memo' }
      ]
    }
  ]

  const dateFormat = 'YYYY/MM/DD'

  return (
    <Row justify={'center'} align={'top'} className="w-full" gutter={[24, 24]}>
      {COMPANY_DATA_PLACEHOLDER.map((section) => (
        <Col xs={12} xl={6} key={section.title}>
          <List
            header={<div className="font-bold">{section.title}</div>}
            bordered
            dataSource={section.column}
            renderItem={(item) => (
              <List.Item style={{ background: '#FFFFFF' }}>
                {item.type === 'date' &&
                  <DatePicker
                    placeholder={item.label}
                    format={dateFormat}
                    className="w-full"
                    status={errors[item.name] ? 'error' : undefined}
                    value={item.value ? dayjs(item.value) : null}
                    onChange={(date, dateString) => {
                      const name = item.name as keyof CompanyRequestType
                      setCompanyInfo((prev) => ({ ...prev, [name]: dateString }))
                    }}
                  />}
                {item.type === 'money' &&
                  <InputNumber<number>
                    prefix='￥'
                    placeholder={item.label}
                    controls={false}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    className="w-full"
                    status={errors[item.name] ? 'error' : undefined}
                    value={item.value === 0 || item.value === null || item.value === undefined ? null : Number(item.value)}
                    onChange={(value) => {
                      const name = item.name as keyof CompanyRequestType
                      setCompanyInfo((prev) => ({ ...prev, [name]: value }))
                    }}
                  />}
                {(item.type === 'text' || item.type === 'email') &&
                  <Input
                    placeholder={item.label}
                    type={item.type}
                    name={item.name}
                    value={item.value || ''}
                    status={errors[item.name] ? 'error' : undefined}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { name, value } = e.target
                      setCompanyInfo((prev) => ({ ...prev, [name as keyof CompanyRequestType]: value }))
                    }}
                  />}
                {item.type === 'textarea' &&
                  <TextArea
                    rows={6}
                    name={item.name}
                    placeholder={item.label}
                    value={item.value as string}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      const { name, value } = e.target
                      setCompanyInfo((prev) => ({ ...prev, [name as keyof CompanyRequestType]: value }))
                    }}
                  />}
              </List.Item>)}
            className="overflow-hidden bg-[#FAFAFA]"
          />
        </Col>
      ))}
    </Row>
  )
}
